/**
 * MessageTemplateFormModal — corp's Add / Edit SMS template and Add / Edit
 * email template dialogs, for one account:
 *   - Name (≤100), Description (≤200);
 *   - SMS: Message, with Insert Shortcode at the caret;
 *   - Email: Subject and Content (corp's editor, RichTextEditor: Visual or
 *     Source, with Insert Shortcode).
 * There is no Category field: a template is added from its category's row,
 * and moved by dragging it on the board (as Products). Name, Message /
 * Subject / Content are required (corp's rules). An email body holding only
 * an image or a table counts as content (corp counted text only). Edits are
 * tracked (pills, Revert, the discard prompt).
 * On a Locked page AdminFormModal makes it a viewer; this form also drops
 * Insert Shortcode and the wording about what is required (RichTextEditor
 * drops its own toolbar).
 */
import { useRef, useState } from 'react';
import { Braces } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { getErrorMessage } from '@/services/apiClient';
import {
  createMessageTemplate,
  htmlHasContent,
  updateMessageTemplate,
  type MessageTemplate,
  type MessageTemplateKind,
} from '@/services/messageTemplatesAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogField, fieldClasses, type FieldState } from './DialogChrome';
import { RichTextEditor } from './RichTextEditor';
import { ShortcodeDialog } from './ShortcodeDialog';
import { useTrackedForm } from './useTrackedForm';

export const MESSAGE_TEMPLATE_NOUN: Record<MessageTemplateKind, string> = { sms: 'SMS template', email: 'email template' };
const TITLE_NOUN: Record<MessageTemplateKind, string> = { sms: 'SMS Template', email: 'Email Template' };
const TOAST_NOUN: Record<MessageTemplateKind, string> = { sms: 'SMS template', email: 'Email template' };

interface Form {
  name: string;
  description: string;
  message: string;
  subject: string;
  content: string;
}

function formFrom(t: MessageTemplate | null): Form {
  return {
    name: t?.name ?? '',
    description: t?.description ?? '',
    message: t?.message ?? '',
    subject: t?.subject ?? '',
    content: t?.content ?? '',
  };
}

export function messageTemplateErrors(kind: MessageTemplateKind, f: Form): string[] {
  const errors: string[] = [];
  if (!f.name.trim()) errors.push('Name is required');
  if (kind === 'sms') {
    if (!f.message.trim()) errors.push('Message is required');
  } else {
    if (!f.subject.trim()) errors.push('Subject is required');
    if (!htmlHasContent(f.content)) errors.push('Content is required');
  }
  return errors;
}

export interface MessageTemplateFormModalProps {
  kind: MessageTemplateKind;
  /** Null to add one to `category`. */
  template: MessageTemplate | null;
  /** Where a new template goes (and the name shown for an existing one's category). */
  category: { id: number; name: string } | null;
  onClose: () => void;
  onSaved: (saved: MessageTemplate | null, isNew: boolean) => void;
}

export function MessageTemplateFormModal({ kind, template, category, onClose, onSaved }: MessageTemplateFormModalProps) {
  const isNew = template === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<Form>(() => formFrom(template), { tracking: !isNew });
  const { form } = t;
  const [saving, setSaving] = useState(false);
  const [shortcodeOpen, setShortcodeOpen] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);

  const errors = messageTemplateErrors(kind, form);
  const state = (missing: boolean): FieldState => (missing ? 'error' : 'none');
  const missingName = !form.name.trim();
  const missingMessage = !form.message.trim();
  const missingSubject = !form.subject.trim();
  const missingContent = !htmlHasContent(form.content);

  /** Insert Shortcode: the code and a space at the caret, the caret after it (as corp). */
  function insertIntoMessage(code: string) {
    const el = messageRef.current;
    const start = el?.selectionStart ?? form.message.length;
    const end = el?.selectionEnd ?? form.message.length;
    const text = `${code} `;
    t.patch({ message: form.message.slice(0, start) + text + form.message.slice(end) });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    });
  }

  async function handleSave() {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    try {
      if (template) {
        await updateMessageTemplate(kind, template.id, form);
        toast.success(`${TOAST_NOUN[kind]} updated`);
        onSaved({ ...template, ...form, name: form.name.trim(), description: form.description.trim(), subject: form.subject.trim() }, false);
      } else {
        if (!category) return;
        const saved = await createMessageTemplate(kind, { ...form, categoryId: category.id });
        toast.success(`${TOAST_NOUN[kind]} created`);
        onSaved(saved.id ? saved : null, true);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  }

  const where = category ? `In ${category.name}. ` : '';
  const description = locked
    ? where.trim() || undefined
    : kind === 'sms'
      ? `${where}Name and message are required. Insert Shortcode adds placeholders like \${PatientFirst}.`
      : `${where}Name, subject and content are required. Insert Shortcode adds placeholders like \${PatientFirst}.`;

  return (
    <AdminFormModal
      title={`${isNew ? 'Add' : 'Edit'} ${TITLE_NOUN[kind]}`}
      description={description}
      isNew={isNew}
      dirty={t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size={kind === 'email' ? 'xl' : 'lg'}
    >
      <div className="grid gap-4">
        <DialogField
          label="Name"
          htmlFor="message-template-name"
          required
          state={state(missingName)}
          labelHint={missingName ? '— required' : undefined}
          badge={t.badge('name', 'Name')}
        >
          <Input
            id="message-template-name"
            value={form.name}
            maxLength={100}
            placeholder="e.g. Appointment reminder"
            onChange={(e) => t.patch({ name: e.target.value })}
            className={fieldClasses.input(state(missingName))}
          />
        </DialogField>

        <DialogField label="Description" htmlFor="message-template-description" badge={t.badge('description', 'Description')}>
          <Input
            id="message-template-description"
            value={form.description}
            maxLength={200}
            placeholder="e.g. Reminder 24h before"
            onChange={(e) => t.patch({ description: e.target.value })}
          />
        </DialogField>

        {kind === 'sms' ? (
          <DialogField
            label="Message"
            htmlFor="message-template-message"
            required
            state={state(missingMessage)}
            labelHint={missingMessage ? '— required' : undefined}
            badge={
              <>
                {t.badge('message', 'Message')}
                {!locked && (
                  <Button
                    variant="secondary"
                    size="xs"
                    className="ml-auto"
                    leftIcon={<Braces className="h-3.5 w-3.5" />}
                    onClick={() => setShortcodeOpen(true)}
                  >
                    Insert Shortcode
                  </Button>
                )}
              </>
            }
          >
            <Textarea
              id="message-template-message"
              ref={messageRef}
              rows={5}
              value={form.message}
              placeholder="Hi ${PatientFirst}, your appointment is tomorrow..."
              onChange={(e) => t.patch({ message: e.target.value })}
              className={`resize-y ${fieldClasses.input(state(missingMessage))}`}
            />
          </DialogField>
        ) : (
          <>
            <DialogField
              label="Subject"
              htmlFor="message-template-subject"
              required
              state={state(missingSubject)}
              labelHint={missingSubject ? '— required' : undefined}
              badge={t.badge('subject', 'Subject')}
            >
              <Input
                id="message-template-subject"
                value={form.subject}
                maxLength={200}
                placeholder="e.g. Your appointment reminder"
                onChange={(e) => t.patch({ subject: e.target.value })}
                className={fieldClasses.input(state(missingSubject))}
              />
            </DialogField>
            <DialogField
              label="Content"
              required
              state={state(missingContent)}
              labelHint={missingContent ? '— required' : undefined}
              badge={t.badge('content', 'Content')}
            >
              <RichTextEditor
                id="message-template-content"
                shortcodes
                value={form.content}
                onChange={(html) => t.patch({ content: html })}
                invalid={missingContent}
                placeholder="Hi ${PatientFirst}, your appointment is tomorrow..."
              />
            </DialogField>
          </>
        )}
      </div>

      {shortcodeOpen && (
        <ShortcodeDialog
          onClose={() => setShortcodeOpen(false)}
          onInsert={(code) => {
            insertIntoMessage(code);
            setShortcodeOpen(false);
          }}
        />
      )}
    </AdminFormModal>
  );
}
